import { N, round, ceil } from './helpers.mjs';

export const ROOFERS = [
  // ====================== ROOFERS ======================
  {
    slug: 'roof-tiles',
    trade: 'roofers',
    name: 'Roof Tiles',
    blurb: 'Default 60 tiles/m² (plain). Concrete interlocking ~10/m².',
    inputs: [
      { key: 'area', label: 'Roof area', unit: 'm²', placeholder: '60' },
      { key: 'perM2', label: 'Tiles per m²', unit: '', placeholder: '60', default: 60 },
      { key: 'wastage', label: 'Wastage', unit: '%', placeholder: '5', default: 5 },
    ],
    compute: (v) => {
      const tiles = ceil(N(v.area) * (N(v.perM2) || 60) * (1 + N(v.wastage) / 100));
      return [
        { label: 'Tiles', value: tiles, unit: 'tiles' },
        { label: 'Roof battens (25×50)', value: ceil(N(v.area) * 3), unit: 'm' },
      ];
    },
  },
  {
    slug: 'roofing-felt',
    trade: 'roofers',
    name: 'Roofing Felt / Membrane',
    blurb: '1m × 50m roll = 50 m². Add 100mm laps.',
    inputs: [
      { key: 'area', label: 'Roof area', unit: 'm²', placeholder: '60' },
    ],
    compute: (v) => {
      const m2 = N(v.area) * 1.1;
      return [
        { label: 'Felt required', value: round(m2, 1), unit: 'm²' },
        { label: 'Rolls (50m²)', value: ceil(m2 / 50), unit: 'rolls' },
      ];
    },
  },

  {
    slug: 'roof-rafters',
    trade: 'roofers',
    name: 'Roof Rafters',
    blurb: 'Common rafter length, ridge rise, birdsmouth position and rafter count for a pitched roof. Enter span (wall plate to wall plate) and pitch.',
    inputs: [
      { key: 'span', label: 'Span (wall plate to wall plate)', unit: 'm', placeholder: '8' },
      { key: 'roofLength', label: 'Roof length', unit: 'm', placeholder: '10' },
      { key: 'pitch', label: 'Roof pitch', unit: '°', placeholder: '35', default: 35 },
      { key: 'eaves', label: 'Eaves overhang', unit: 'mm', placeholder: '300', default: 300 },
      { key: 'centres', label: 'Rafter centres', unit: 'mm', placeholder: '400', default: 400 },
    ],
    compute: (v) => {
      const span = N(v.span);
      const pitch = N(v.pitch);
      if (span <= 0 || pitch <= 0 || pitch >= 90) {
        return [{ label: 'Enter span and a valid pitch (1–89°)', value: 0, unit: '' }];
      }
      const rad = (pitch * Math.PI) / 180;
      const run = span / 2;
      const rise = run * Math.tan(rad);
      const bodyLen = run / Math.cos(rad); // ridge plumb cut to birdsmouth (over wall plate)
      const rafterLen = (run + N(v.eaves) / 1000) / Math.cos(rad); // incl. overhang
      const centres = (N(v.centres) || 400) / 1000;
      const pairs = ceil(N(v.roofLength) / centres) + 1;
      const totalRafters = pairs * 2;
      return [
        { label: 'Run (per side)', value: round(run, 2), unit: 'm' },
        { label: 'Ridge rise (above plate)', value: round(rise, 2), unit: 'm' },
        { label: 'Rafter length (incl. overhang)', value: round(rafterLen, 2), unit: 'm' },
        { label: 'Birdsmouth from ridge (along rafter)', value: round(bodyLen, 2), unit: 'm' },
        { label: 'Number of rafters (both sides)', value: totalRafters, unit: 'pcs' },
        { label: 'Total rafter timber', value: round(totalRafters * rafterLen, 1), unit: 'm' },
      ];
    },
  },
  {
    slug: 'roof-trusses',
    trade: 'roofers',
    name: 'Roof Truss Quantity',
    blurb: 'Number of trusses for a roof length at standard centres (usually 600mm), including the gable-end truss.',
    inputs: [
      { key: 'roofLength', label: 'Roof length', unit: 'm', placeholder: '10' },
      {
        key: 'centres',
        label: 'Truss centres',
        type: 'select',
        default: '600',
        options: [
          { value: '600', label: '600mm (standard trussed rafters)' },
          { value: '400', label: '400mm (heavy loading / tile battens)' },
        ],
      },
    ],
    compute: (v) => {
      const centres = (parseFloat(v.centres) || 600) / 1000;
      const len = N(v.roofLength);
      if (len <= 0) return [{ label: 'Enter roof length', value: 0, unit: '' }];
      const trusses = ceil(len / centres) + 1;
      return [
        { label: 'Trusses required', value: trusses, unit: 'pcs' },
        { label: 'Spacing', value: parseFloat(v.centres) || 600, unit: 'mm' },
      ];
    },
  },
  {
    slug: 'roof-pitch',
    trade: 'roofers',
    name: 'Roof Pitch Converter',
    blurb: 'Convert roof pitch between degrees, rise-over-run, ratio and percentage.',
    inputs: [
      {
        key: 'mode',
        label: 'Work from',
        type: 'select',
        default: 'degrees',
        options: [
          { value: 'degrees', label: 'Angle in degrees' },
          { value: 'rise-run', label: 'Rise & run measurements' },
        ],
      },
      { key: 'pitch', label: 'Pitch angle (degrees mode)', unit: '°', placeholder: '35', default: 35 },
      { key: 'rise', label: 'Rise (rise-run mode)', unit: 'mm', placeholder: '2000', default: 0 },
      { key: 'run', label: 'Run (rise-run mode)', unit: 'mm', placeholder: '4000', default: 0 },
    ],
    compute: (v) => {
      let deg;
      if (v.mode === 'rise-run') {
        const rise = N(v.rise); const run = N(v.run);
        if (rise <= 0 || run <= 0) return [{ label: 'Enter rise and run', value: 0, unit: '' }];
        deg = Math.atan(rise / run) * 180 / Math.PI;
      } else {
        deg = N(v.pitch);
        if (deg <= 0 || deg >= 90) return [{ label: 'Enter a pitch of 1–89°', value: 0, unit: '' }];
      }
      const t = Math.tan(deg * Math.PI / 180);
      const ratioN = t > 0 ? 1 / t : 0; // 1 : n  (1 vertical to n horizontal)
      return [
        { label: 'Pitch angle', value: round(deg, 1), unit: '°' },
        { label: 'Ratio (rise : run)', value: `1 : ${round(ratioN, 2)}`, unit: '' },
        { label: 'Percentage', value: round(t * 100, 1), unit: '%' },
        { label: 'Rise per metre run', value: round(t * 1000, 0), unit: 'mm/m' },
        { label: 'Rise per 12 (x:12)', value: round(t * 12, 1), unit: 'in 12' },
      ];
    },
  },

];
